import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";
import { endpoints } from "@/lib/api";
import { displayReqNumber } from "@/lib/format";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function ApproveToken() {
  const { token } = useParams();
  const { brand } = useBranding();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    endpoints.resolveToken(token)
      .then((d) => { setOrder(d); setLoading(false); })
      .catch(() => { setError("Invalid or expired link"); setLoading(false); });
  }, [token]);

  const decide = async (decision) => {
    setSubmitting(true);
    try {
      const r = await endpoints.decideToken(token, { decision, note, actor_name: order?.approver_name });
      setResult({ decision, order: r.order });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not process decision");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="approve-token-page">
      <div className="stripe-bar h-1.5 w-full" />
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 grid place-items-center bg-[#0033A0] text-white font-black text-lg" style={{ clipPath: "polygon(0 0, 100% 0, 100% 60%, 50% 100%, 0 60%)" }}>C</div>
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{brand.small}</div>
            <div className="font-display font-bold text-base text-slate-900 -mt-0.5">Flexspace Approval</div>
          </div>
        </div>
      </header>

      <main className="flex-1 corp-grid">
        <div className="max-w-2xl mx-auto px-6 py-14">
          {loading && <div className="text-center text-slate-500"><Loader2 className="animate-spin mx-auto mb-2" /> Loading…</div>}
          {error && (
            <div className="bg-white border border-red-200 rounded-md p-8 text-center" data-testid="token-error">
              <XCircle className="mx-auto text-[#DA291C] mb-3" size={40} />
              <div className="font-display font-bold text-xl text-slate-900">{error}</div>
              <div className="text-sm text-slate-500 mt-1">This approval link may have already been actioned or expired.</div>
            </div>
          )}

          {order && !result && (
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
                <ShieldCheck className="text-[#0033A0]" size={24} />
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Approval Requested</div>
                  <div className="font-display font-bold text-xl text-slate-900">Hi {order.approver_name}, please review this request</div>
                </div>
              </div>

              <div className="p-6 space-y-5">
                <div className="grid md:grid-cols-[120px_1fr] gap-4">
                  <img src={order.device_image} alt="" className="w-full h-24 md:h-full object-cover rounded-md border border-slate-200" />
                  <div>
                    <div className="font-mono text-xs font-bold text-[#0033A0]">{displayReqNumber(order)}</div>
                    <div className="font-display font-bold text-lg text-slate-900 mt-1">{order.device_name}</div>
                    <div className="text-sm text-slate-500">Requested by <span className="font-medium text-slate-900">{order.user_name}</span></div>
                    <div className="text-sm text-slate-500">Delivery: {order.delivery_location} · Priority: {order.priority}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-slate-200 rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total</div>
                    <div className="font-display font-black text-2xl text-slate-900">${order.total_amount.toLocaleString()}</div>
                  </div>
                  <div className="border border-slate-200 rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Est. Delivery</div>
                    <div className="text-sm text-slate-900 mt-1">Within 24h of approval</div>
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Business Justification</div>
                  <div className="mt-1 text-sm text-slate-700 border-l-2 border-slate-200 pl-3 italic">&ldquo;{order.justification}&rdquo;</div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Note (optional)</div>
                  <Textarea
                    data-testid="token-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Attach a note to your decision…"
                    className="mt-1 min-h-20"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-slate-200 bg-slate-50 flex gap-2 justify-end">
                <Button
                  variant="outline"
                  data-testid="token-reject"
                  onClick={() => decide("reject")}
                  disabled={submitting}
                  className="border-[#DA291C] text-[#DA291C] hover:bg-[#DA291C] hover:text-white"
                >
                  Reject
                </Button>
                <Button
                  data-testid="token-approve"
                  onClick={() => decide("approve")}
                  disabled={submitting}
                  className="bg-[#0033A0] hover:bg-[#002573] text-white"
                >
                  {submitting && <Loader2 size={14} className="animate-spin mr-2" />} Approve
                </Button>
              </div>
            </div>
          )}

          {result && (
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-10 text-center" data-testid="token-result">
              {result.decision === "approve" ? (
                <CheckCircle2 className="mx-auto text-green-500 mb-3" size={48} />
              ) : (
                <XCircle className="mx-auto text-[#DA291C] mb-3" size={48} />
              )}
              <div className="font-display font-black text-2xl text-slate-900">
                {result.decision === "approve" ? "Approved" : "Rejected"}
              </div>
              <div className="text-sm text-slate-500 mt-1">
                {displayReqNumber(result.order)} · {result.order.user_name}
              </div>
              <div className="text-sm text-slate-600 mt-3">
                {result.decision === "approve" ? "The requester has been notified. IT will fulfill within 24 hours." : "The requester has been notified of the rejection."}
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-3 text-xs text-slate-500 flex justify-between">
          <span>{brand.footer}</span>
          <span>Flexspace Approval</span>
        </div>
      </footer>
    </div>
  );
}
